import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, LogOut, Save, BarChart3, ArrowLeft } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";
import { PdfUpload } from "@/components/PdfUpload";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { BackupPanel } from "@/components/BackupPanel";
import { getSupportStatus, DAY_LABELS } from "@/lib/hours";

const ENTITIES = {
  devices: { label: "Dispositivos", fields: [
    { k: "name", t: "text" }, { k: "slug", t: "text" }, { k: "icon", t: "text", hint: "Tv, Box, Flame, Tv2, Smartphone, Apple, Laptop, Cast, Gamepad2" },
    { k: "description", t: "textarea" }, { k: "badge", t: "text" }, { k: "order", t: "number" }] },
  brands: { label: "Marcas TV", fields: [
    { k: "name", t: "text" }, { k: "system", t: "text" }, { k: "compatibility", t: "select", opts: ["Alta", "Média", "Baixa"] },
    { k: "recommended_apps", t: "list" }, { k: "tutorial_slug", t: "text" }, { k: "order", t: "number" }] },
  apps: { label: "Aplicativos", fields: [
    { k: "name", t: "text" }, { k: "icon", t: "text", hint: "Play, Box, Sparkles, Tv, Smartphone" }, { k: "platforms", t: "list" },
    { k: "description", t: "textarea" }, { k: "difficulty", t: "select", opts: ["Fácil", "Médio", "Difícil"] },
    { k: "tutorial_slug", t: "text" }, { k: "order", t: "number" }] },
  tutorials: { label: "Tutoriais", fields: [
    { k: "title", t: "text" }, { k: "slug", t: "text" }, { k: "device", t: "text" }, { k: "app", t: "text" },
    { k: "category", t: "text" }, { k: "time_estimate", t: "text" }, { k: "difficulty", t: "select", opts: ["Fácil", "Médio", "Difícil"] },
    { k: "order", t: "number" }, { k: "pdf_url", t: "pdf", hint: "Opcional. Sem arquivo, o PDF é gerado automaticamente." }, { k: "steps", t: "steps" }] },
  videos: { label: "Vídeos", fields: [
    { k: "title", t: "text" }, { k: "youtube_id", t: "text", hint: "ID do YouTube (ex: dQw4w9WgXcQ)" },
    { k: "category", t: "select", opts: ["Smart TV", "Android", "TV Box", "Celular", "Computador"] },
    { k: "thumbnail", t: "image", hint: "Opcional. Se vazio, usa a capa do YouTube." }, { k: "order", t: "number" }] },
  faqs: { label: "FAQ", fields: [{ k: "question", t: "text" }, { k: "answer", t: "textarea" }, { k: "order", t: "number" }] },
  status: { label: "Status", fields: [
    { k: "name", t: "text" }, { k: "status", t: "select", opts: ["online", "maintenance", "instability"] },
    { k: "message", t: "textarea" }, { k: "order", t: "number" }] },
  announcements: { label: "Comunicados", fields: [
    { k: "title", t: "text" }, { k: "message", t: "textarea" }, { k: "type", t: "select", opts: ["info", "update", "maintenance", "new"] },
    { k: "active", t: "bool" }, { k: "popup", t: "bool", hint: "Aparece em destaque ao abrir o site (1x por sessão)" }] },
};

const LABELS = { name: "Nome", slug: "Slug", icon: "Ícone", description: "Descrição", badge: "Selo", order: "Ordem",
  system: "Sistema", compatibility: "Compatibilidade", recommended_apps: "Apps recomendados", tutorial_slug: "Slug do tutorial",
  platforms: "Plataformas", difficulty: "Dificuldade", title: "Título", device: "Dispositivo", app: "Aplicativo",
  category: "Categoria", time_estimate: "Tempo estimado", steps: "Passos", youtube_id: "YouTube ID", question: "Pergunta",
  answer: "Resposta", status: "Status", message: "Mensagem", type: "Tipo", active: "Ativo", popup: "Destaque (popup)", thumbnail: "Miniatura", pdf_url: "Tutorial em PDF" };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("devices");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => { if (user === false) nav("/admin"); }, [user, nav]);

  const load = useCallback((t) => api.get(`/${t}`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])), []);
  useEffect(() => {
    if (!tab) return;
    setShowAnalytics(false);
    setItems([]);
    if (tab !== "settings") load(tab);
  }, [tab, load]);

  const emptyItem = () => {
    const o = {};
    ENTITIES[tab].fields.forEach((f) => {
      o[f.k] = f.t === "list" ? [] : f.t === "steps" ? [] : f.t === "number" ? 0 : f.t === "bool" ? f.k === "active" : "";
    });
    return o;
  };

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/${tab}/${data.id}`, data);
      else await api.post(`/admin/${tab}`, data);
      toast.success("Salvo com sucesso!");
      setEditing(null);
      load(tab);
    } catch (e) { toast.error("Erro ao salvar. Verifique os campos."); }
  };

  const remove = async () => {
    const id = toDelete?.id;
    setToDelete(null);
    try {
      await api.delete(`/admin/${tab}/${id}`);
      toast.success("Excluído.");
      load(tab);
    } catch { toast.error("Erro ao excluir."); }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-gray-400">Carregando...</div>;

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <header className="sticky top-0 z-40 bg-[#0B0F17]/90 backdrop-blur border-b border-slate-800 px-4 sm:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" className="h-9 w-9 object-contain" alt="logo" />
          <span className="font-bold">Painel <span className="ci-cyan-text">Admin</span></span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav("/")} className="text-sm text-gray-400 hover:text-white px-3 py-2 rounded-lg hover:bg-white/5 inline-flex items-center gap-1.5" data-testid="admin-view-site">
            <ArrowLeft className="w-4 h-4" /> Ver site
          </button>
          <button onClick={() => { logout(); nav("/admin"); }} data-testid="admin-logout-btn"
            className="text-sm text-red-300 hover:bg-red-500/10 px-3 py-2 rounded-lg inline-flex items-center gap-1.5">
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {Object.entries(ENTITIES).map(([k, v]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition ${tab === k && !showAnalytics ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}>
              {v.label}
            </button>
          ))}
          <button onClick={() => { setShowAnalytics(false); setTab("settings"); }} data-testid="admin-tab-settings"
            className={`px-4 py-2 rounded-full text-sm font-medium border transition ${tab === "settings" ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}>
            Configurações
          </button>
          <button onClick={() => { setShowAnalytics(true); setTab(""); }} data-testid="admin-tab-analytics"
            className={`px-4 py-2 rounded-full text-sm font-medium border transition inline-flex items-center gap-1.5 ${showAnalytics ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "bg-white/5 text-gray-300 border-white/10 hover:bg-white/10"}`}>
            <BarChart3 className="w-4 h-4" /> Analytics
          </button>
        </div>

        {showAnalytics ? <Analytics /> : tab === "settings" ? <SettingsPanel /> : (
          <>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">{ENTITIES[tab].label}</h2>
              <button onClick={() => setEditing(emptyItem())} data-testid="admin-add-btn"
                className="ci-btn-gold inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-semibold text-sm hover:brightness-105 transition">
                <Plus className="w-4 h-4" /> Adicionar
              </button>
            </div>
            <div className="grid gap-3">
              {items.map((it) => (
                <div key={it.id} data-testid={`admin-item-${it.id}`} className="ci-card p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{it.name || it.title || it.question}</div>
                    <div className="text-xs text-gray-500 truncate">{it.description || it.message || it.answer || it.category || it.system || ""}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => setEditing(it)} data-testid={`admin-edit-${it.id}`} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-cyan-300"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => setToDelete(it)} data-testid={`admin-delete-${it.id}`} className="p-2 rounded-lg bg-white/5 hover:bg-red-500/20 text-red-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div className="text-gray-500 text-sm py-8 text-center">Nenhum item. Clique em Adicionar.</div>}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        itemName={toDelete?.name || toDelete?.title || toDelete?.question} />
      {editing && <EditorModal entity={ENTITIES[tab]} data={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function EditorModal({ entity, data, onClose, onSave }) {
  const [form, setForm] = useState(data);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="ci-card p-6 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="admin-editor-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-bold text-lg">{form.id ? "Editar" : "Novo"} item</h3>
          <button onClick={onClose} data-testid="admin-editor-close" className="p-2 rounded-lg bg-white/5 hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1 pb-6">
          {entity.fields.map((f) => (
            <div key={f.k}>
              <label className="block text-sm text-gray-300 mb-1">{LABELS[f.k] || f.k}{f.hint && <span className="text-gray-400 text-xs ml-2">{f.hint}</span>}</label>
              <FieldInput f={f} value={form[f.k]} onChange={(v) => set(f.k, v)} form={form} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} data-testid="admin-editor-cancel" className="px-4 py-2.5 rounded-lg border border-white/10 text-gray-300 hover:bg-white/5">Cancelar</button>
          <button onClick={() => onSave(form)} data-testid="admin-save-btn" className="ci-btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold hover:brightness-105"><Save className="w-4 h-4" /> Salvar</button>
        </div>
      </div>
    </div>
  );
}

const FIELD_RENDERERS = {
  textarea: ({ f, value, onChange, cls }) => <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={cls} data-testid={`field-${f.k}`} />,
  number: ({ f, value, onChange, cls }) => <input type="number" value={value ?? 0} onChange={(e) => onChange(Number(e.target.value))} className={cls} data-testid={`field-${f.k}`} />,
  select: ({ f, value, onChange, cls }) => (
    <select value={value || f.opts[0]} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${f.k}`}>
      {f.opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  ),
  bool: ({ f, value, onChange }) => (
    <button type="button" onClick={() => onChange(!value)} data-testid={`field-${f.k}`}
      className={`px-4 py-2 rounded-lg text-sm font-medium border ${value ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-gray-400 border-white/10"}`}>
      {value ? "Ativo" : "Inativo"}
    </button>
  ),
  list: ({ f, value, onChange, cls }) => (
    <input value={Array.isArray(value) ? value.join(", ") : ""} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
      placeholder="Separe por vírgula" className={cls} data-testid={`field-${f.k}`} />
  ),
  steps: ({ value, onChange }) => <StepsEditor value={value || []} onChange={onChange} />,
  image: ({ f, value, onChange }) => <ImageUpload value={value} onChange={onChange} testid={`field-${f.k}`} />,
  pdf: ({ value, onChange, form }) => <PdfUpload value={value} onChange={onChange} slug={form?.slug} />,
  text: ({ f, value, onChange, cls }) => <input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${f.k}`} />,
};

function FieldInput({ f, value, onChange, form }) {
  const cls = "w-full bg-[#0B0F17] border border-white/10 focus:border-cyan-400/60 rounded-lg px-3 py-2.5 outline-none text-sm";
  const Render = FIELD_RENDERERS[f.t] || FIELD_RENDERERS.text;
  return <Render f={f} value={value} onChange={onChange} cls={cls} form={form} />;
}

function StepsEditor({ value, onChange }) {
  const upd = (i, k, v) => { const c = [...value]; c[i] = { ...c[i], [k]: v }; onChange(c); };
  const add = () => onChange([...value, { key: `s${Date.now()}`, title: "", description: "", image: "" }]);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));
  const cls = "w-full bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-2 outline-none text-sm";
  return (
    <div className="space-y-3">
      {value.map((s, i) => (
        <div key={s.key || `${i}-${s.title}`} className="border border-white/10 rounded-lg p-3 space-y-2" data-testid={`step-editor-${i}`}>
          <div className="flex items-center justify-between"><span className="text-xs text-amber-400 font-semibold">Passo {i + 1}</span>
            <button type="button" onClick={() => del(i)} className="text-red-300 text-xs">Remover</button></div>
          <input placeholder="Título" value={s.title || ""} onChange={(e) => upd(i, "title", e.target.value)} className={cls} />
          <textarea placeholder="Descrição" value={s.description || ""} onChange={(e) => upd(i, "description", e.target.value)} rows={2} className={cls} />
          <ImageUpload value={s.image} onChange={(v) => upd(i, "image", v)} testid={`step-image-${i}`} />
        </div>
      ))}
      <button type="button" onClick={add} data-testid="add-step-btn" className="text-sm text-cyan-300 inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Adicionar passo</button>
    </div>
  );
}

function SettingsPanel() {
  const { settings, setSettings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  useEffect(() => { setForm(settings); }, [settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDay = (d, k, v) => setForm((f) => ({ ...f, hours: { ...f.hours, [d]: { ...(f.hours?.[d] || { enabled: true, open: "09:00", close: "22:00" }), [k]: v } } }));
  const save = async () => {
    try {
      const r = await api.put("/admin/settings", form);
      setSettings(r.data);
      refresh();
      toast.success("Configurações salvas!");
    } catch { toast.error("Erro ao salvar."); }
  };
  const cls = "w-full bg-[#0B0F17] border border-white/10 focus:border-cyan-400/60 rounded-lg px-3 py-2.5 outline-none text-sm";
  const preview = getSupportStatus(form);
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return (
    <div className="grid gap-6 lg:grid-cols-2 items-start">
      <div className="ci-card p-6">
        <h2 className="text-xl font-bold mb-1">Configurações do WhatsApp</h2>
        <p className="text-sm text-gray-400 mb-5">Altere o número e a mensagem usada em todos os botões do site.</p>
        <label className="block text-sm text-gray-300 mb-1">Número (somente dígitos com DDI/DDD)</label>
        <input value={form.whatsapp_number || ""} onChange={(e) => set("whatsapp_number", e.target.value)} className={cls + " mb-4"} data-testid="settings-whatsapp-number" placeholder="5521973193851" />
        <label className="block text-sm text-gray-300 mb-1">Mensagem automática</label>
        <textarea value={form.whatsapp_message || ""} onChange={(e) => set("whatsapp_message", e.target.value)} rows={2} className={cls} data-testid="settings-whatsapp-message" />
      </div>
      <div className="ci-card p-6">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="text-xl font-bold">Horário de atendimento</h2>
          <span data-testid="settings-hours-preview" className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${preview.online ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" : "bg-white/5 border-white/10 text-gray-400"}`}>{preview.label}</span>
        </div>
        <p className="text-sm text-gray-400 mb-4">Mostrado no botão do WhatsApp (fuso: {form.timezone || "America/Sao_Paulo"}).</p>
        <button type="button" onClick={() => set("always_open", !form.always_open)} data-testid="settings-always-open"
          className={`mb-4 px-4 py-2 rounded-lg text-sm font-medium border ${form.always_open ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-white/5 text-gray-400 border-white/10"}`}>
          {form.always_open ? "Atendimento 24h ativado" : "Atendimento 24h desativado"}
        </button>
        {!form.always_open && (
          <div className="space-y-2">
            {days.map((d) => {
              const h = form.hours?.[d] || { enabled: true, open: "09:00", close: "22:00" };
              return (
                <div key={d} className="flex items-center gap-2 text-sm" data-testid={`settings-day-${d}`}>
                  <button type="button" onClick={() => setDay(d, "enabled", !h.enabled)} data-testid={`settings-day-${d}-toggle`}
                    className={`w-24 text-left px-2.5 py-1.5 rounded-lg border text-xs font-medium ${h.enabled ? "bg-cyan-400/10 border-cyan-400/30 text-cyan-300" : "bg-white/5 border-white/10 text-gray-500 line-through"}`}>
                    {DAY_LABELS[d]}
                  </button>
                  <input type="time" value={h.open} disabled={!h.enabled} onChange={(e) => setDay(d, "open", e.target.value)} className={cls + " disabled:opacity-40"} data-testid={`settings-day-${d}-open`} />
                  <span className="text-gray-500">às</span>
                  <input type="time" value={h.close} disabled={!h.enabled} onChange={(e) => setDay(d, "close", e.target.value)} className={cls + " disabled:opacity-40"} data-testid={`settings-day-${d}-close`} />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <ClosuresEditor value={form.closures || []} onChange={(v) => set("closures", v)} />
      <BackupPanel onRestored={refresh} />
      <div className="lg:col-span-2">
        <button onClick={save} data-testid="settings-save-btn" className="ci-btn-gold inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold hover:brightness-105"><Save className="w-4 h-4" /> Salvar</button>
      </div>
    </div>
  );
}

function ClosuresEditor({ value, onChange }) {
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  const cls = "bg-[#0B0F17] border border-white/10 focus:border-cyan-400/60 rounded-lg px-3 py-2.5 outline-none text-sm";
  const today = new Date().toISOString().slice(0, 10);
  const add = () => {
    if (!date) return toast.error("Escolha uma data.");
    if (value.some((c) => c.date === date)) return toast.error("Data já cadastrada.");
    onChange([...value, { date, label: label.trim() || "Feriado" }].sort((a, b) => a.date.localeCompare(b.date)));
    setDate(""); setLabel("");
  };
  const fmt = (d) => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
  return (
    <div className="ci-card p-6 lg:col-span-2" data-testid="settings-closures">
      <h2 className="text-xl font-bold mb-1">Feriados e pausas</h2>
      <p className="text-sm text-gray-400 mb-4">Nestas datas o suporte aparece como fechado, mesmo em dia útil.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <input type="date" value={date} min={today} onChange={(e) => setDate(e.target.value)} className={cls} data-testid="closure-date" />
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Motivo (ex: Natal, Folga da equipe)" className={cls + " flex-1 min-w-[200px]"} data-testid="closure-label" />
        <button type="button" onClick={add} data-testid="closure-add-btn" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-400/20 text-sm font-medium"><Plus className="w-4 h-4" /> Adicionar</button>
      </div>
      {value.length === 0 ? <div className="text-sm text-gray-500">Nenhuma data cadastrada.</div> : (
        <div className="flex flex-wrap gap-2">
          {value.map((c) => (
            <span key={c.date} data-testid={`closure-${c.date}`} className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-full border ${c.date < today ? "bg-white/5 border-white/10 text-gray-500" : "bg-amber-400/10 border-amber-400/30 text-amber-200"}`}>
              <span className="font-semibold">{fmt(c.date)}</span> {c.label}
              <button type="button" onClick={() => onChange(value.filter((x) => x.date !== c.date))} data-testid={`closure-remove-${c.date}`} className="p-0.5 rounded hover:bg-white/10 text-gray-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-gray-400">Carregando métricas...</div>;
  const cards = [
    { label: "Eventos totais", v: data.totals.events },
    { label: "Cliques no WhatsApp", v: data.totals.whatsapp_clicks },
    { label: "Pesquisas", v: data.totals.searches },
    { label: "Visualizações de tutoriais", v: data.totals.tutorial_views },
    { label: "Buscas por voz", v: data.totals.voice_searches ?? 0 },
    { label: "Compartilhamentos", v: data.totals.shares ?? 0 },
    { label: "Backups gerados", v: data.totals.backups ?? 0 },
  ];
  return (
    <div>
      <h2 className="text-xl font-bold mb-5">Analytics</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="ci-card p-5" data-testid={`analytics-${c.label}`}>
            <div className="text-3xl font-extrabold ci-cyan-text">{c.v}</div>
            <div className="text-sm text-gray-400 mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <TopList title="Pesquisas mais realizadas" rows={data.top_searches} />
        <TopList title="Dispositivos mais acessados" rows={data.top_devices} />
        <TopList title="Tutoriais mais acessados" rows={data.top_tutorials} />
        <div className="ci-card p-5">
          <h3 className="font-semibold mb-3">Feedback dos tutoriais</h3>
          <div className="space-y-2">
            {data.feedback.map((f) => (
              <div key={f.title} className="flex items-center justify-between text-sm">
                <span className="text-gray-300 truncate mr-3">{f.title}</span>
                <span className="shrink-0"><span className="text-emerald-300">👍 {f.yes}</span> <span className="text-red-300 ml-2">👎 {f.no}</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function TopList({ title, rows }) {
  return (
    <div className="ci-card p-5">
      <h3 className="font-semibold mb-3">{title}</h3>
      {rows.length === 0 ? <div className="text-sm text-gray-500">Sem dados ainda.</div> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between text-sm">
              <span className="text-gray-300 truncate mr-3">{r.label}</span>
              <span className="text-cyan-300 font-semibold shrink-0">{r.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
